/**
 * @fileoverview Retry state models and configuration for resilience
 * @description Defines retry behavior, error classification, and retry state tracking
 * @project AIEMD Platform - Resilience System
 * @created 2025-01-18
 */

/**
 * Connection quality levels
 */
export enum ConnectionQuality {
  /** Excellent connection (< 100ms latency) */
  EXCELLENT = 'excellent',

  /** Good connection (100-300ms latency) */
  GOOD = 'good',

  /** Fair connection (300-1000ms latency) */
  FAIR = 'fair',

  /** Poor connection (> 1000ms latency) */
  POOR = 'poor',

  /** Unknown quality */
  UNKNOWN = 'unknown',
}

/**
 * Configuration for retry behavior
 */
export interface RetryConfig {
  /** Maximum number of retry attempts (default: 3) */
  maxAttempts: number;

  /** Base delay in milliseconds for first retry (default: 1000ms) */
  baseDelay: number;

  /** Maximum delay in milliseconds between retries (default: 4000ms) */
  maxDelay: number;

  /** Jitter factor to add randomness (0.0 - 1.0, default: 0.1) */
  jitterFactor: number;

  /** HTTP status codes that should trigger a retry */
  retryableStatusCodes: number[];

  /** Error codes/messages that should trigger a retry */
  retryableErrors: string[];

  /** Whether to show toast notifications during retries */
  showNotifications: boolean;

  /** Whether to auto-queue failed mutations for offline retry */
  enableOfflineQueue: boolean;
}

/**
 * Default retry configuration
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000,
  maxDelay: 4000,
  jitterFactor: 0.1,
  retryableStatusCodes: [429, 500, 502, 503, 504],
  retryableErrors: [
    'NETWORK_ERROR',
    'TIMEOUT',
    'ECONNREFUSED',
    'ENOTFOUND',
    'ETIMEDOUT',
    'NetworkError',
    'Failed to fetch',
  ],
  showNotifications: true,
  enableOfflineQueue: true,
};

/**
 * Current state of a retry operation
 */
export interface RetryState {
  /** Current attempt number (0-indexed) */
  currentAttempt: number;

  /** Maximum number of attempts allowed */
  maxAttempts: number;

  /** Whether a retry is currently in progress */
  isRetrying: boolean;

  /** The last error that occurred */
  lastError?: Error;

  /** Milliseconds until next retry attempt */
  nextRetryIn?: number;

  /** Timestamp of last retry attempt */
  lastRetryAt?: number;

  /** Array of all errors encountered during retries */
  errorHistory: Array<{
    error: Error;
    attempt: number;
    timestamp: number;
  }>;
}

/**
 * Error classification result
 */
export interface ErrorClassification {
  /** Whether this error should trigger a retry */
  isRetryable: boolean;

  /** Error category for handling logic */
  category: ErrorCategory;

  /** Original error object */
  error: Error;

  /** HTTP status code if available */
  statusCode?: number;

  /** Suggested action for the user */
  userMessage: string;

  /** Whether to check service health on this error */
  checkHealth: boolean;
}

/**
 * Categories of errors for different handling strategies
 */
export enum ErrorCategory {
  /** Network connectivity issues */
  NETWORK = 'network',

  /** Server errors (5xx) */
  SERVER = 'server',

  /** Rate limiting (429) */
  RATE_LIMIT = 'rate_limit',

  /** Authentication/authorization failures */
  AUTH = 'auth',

  /** Client errors (4xx except 429) */
  CLIENT = 'client',

  /** Timeout errors */
  TIMEOUT = 'timeout',

  /** Unknown/unclassified errors */
  UNKNOWN = 'unknown',
}

/**
 * Result of a retry operation
 */
export interface RetryResult<T> {
  /** Whether the operation succeeded */
  success: boolean;

  /** Result data if successful */
  data?: T;

  /** Error if failed */
  error?: Error;

  /** Number of attempts made */
  attempts: number;

  /** Total time taken in milliseconds */
  totalDuration: number;

  /** Whether max retries were exceeded */
  maxRetriesExceeded: boolean;
}

/**
 * Options for executing a request with retry logic
 */
export interface RetryOptions {
  /** Custom retry configuration for this request */
  config?: Partial<RetryConfig>;

  /** Operation type for tracking and notifications */
  operationType?: string;

  /** Whether to queue this operation if offline */
  queueIfOffline?: boolean;

  /** Custom error classifier function */
  errorClassifier?: (error: Error) => ErrorClassification;

  /** Callback on each retry attempt */
  onRetry?: (attempt: number, delay: number) => void;

  /** Callback when max retries exceeded */
  onMaxRetriesExceeded?: (error: Error) => void;
}

/**
 * Helper functions for retry state management
 */

/**
 * Creates initial retry state
 */
export function createInitialRetryState(maxAttempts: number = 3): RetryState {
  return {
    currentAttempt: 0,
    maxAttempts,
    isRetrying: false,
    errorHistory: [],
  };
}

/**
 * Creates retry state for an active retry
 */
export function createRetryingState(
  currentAttempt: number,
  maxAttempts: number,
  nextRetryIn: number,
  error: Error
): RetryState {
  return {
    currentAttempt,
    maxAttempts,
    isRetrying: true,
    lastError: error,
    nextRetryIn,
    lastRetryAt: Date.now(),
    errorHistory: [],
  };
}

/**
 * Adds error to retry history
 */
export function addErrorToHistory(
  state: RetryState,
  error: Error,
  attempt: number
): RetryState {
  return {
    ...state,
    errorHistory: [
      ...state.errorHistory,
      {
        error,
        attempt,
        timestamp: Date.now(),
      },
    ],
  };
}

/**
 * Check if more retries are available
 */
export function canRetry(state: RetryState): boolean {
  return state.currentAttempt < state.maxAttempts;
}

/**
 * Check if currently retrying
 */
export function isRetrying(state: RetryState): boolean {
  return state.isRetrying;
}

/**
 * Get retry progress as percentage
 */
export function getRetryProgress(state: RetryState): number {
  if (state.maxAttempts === 0) return 100;
  return Math.round((state.currentAttempt / state.maxAttempts) * 100);
}

/**
 * Calculate exponential backoff delay with jitter
 */
export function calculateBackoffDelay(
  attempt: number,
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): number {
  const exponentialDelay = config.baseDelay * Math.pow(2, attempt);
  const cappedDelay = Math.min(exponentialDelay, config.maxDelay);
  const jitter = cappedDelay * config.jitterFactor * Math.random();
  return Math.floor(cappedDelay + jitter);
}

/**
 * Format retry state for display
 */
export function formatRetryState(state: RetryState): string {
  if (!state.isRetrying) return '';

  const nextRetrySeconds = state.nextRetryIn
    ? Math.ceil(state.nextRetryIn / 1000)
    : 0;

  return `Retrying in ${nextRetrySeconds}s... (Attempt ${state.currentAttempt + 1}/${state.maxAttempts})`;
}
